import { CSSProperties, Fragment, KeyboardEvent as ReactKeyboardEvent, ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronRight } from 'lucide-react'
import { POPOVER_Z_INDEX } from './z-index'

export interface DropdownItem {
    label: string
    description?: string
    type?: 'initial' | 'danger'
    prefix?: ReactNode
    suffix?: ReactNode
    checked?: boolean
    submenu?: DropdownSection[]
    disabled?: boolean
    onClick?: () => void
}

export interface DropdownSection {
    title?: string
    items: DropdownItem[]
}

export interface DropdownProps {
    sections: DropdownSection[]
    trigger: ReactNode
    style?: CSSProperties
}

const PANEL_CHROME = 'rounded-3xl bg-surface shadow-overlay backdrop-blur-[20px]'

// `bare` omits the visual chrome (radius, bg, shadow, blur): the top-level panel is wrapped by an element that owns the chrome, so the
// shadow is never drawn inside a scroll container (overflow would clip it into a hard edge).
const DropdownPanel = ({ sections, onClose, bare }: { sections: DropdownSection[]; onClose: () => void; bare?: boolean }) => {
    return (
        <div role="menu" className={`flex min-w-[200px] flex-col gap-0.5 p-1 ${bare ? '' : PANEL_CHROME}`}>
            {sections.map((section, si) => (
                <Fragment key={si}>
                    {si > 0 && (
                        <div className="my-0.5 px-3">
                            <div className="h-px bg-border" />
                        </div>
                    )}
                    {section.title && <div className="px-3 pb-1 pt-2.5 text-xs font-medium leading-4 text-foreground-muted">{section.title}</div>}
                    {section.items.map((item, ii) => (
                        <DropdownItemRow key={`${si}-${ii}`} item={item} onClose={onClose} />
                    ))}
                </Fragment>
            ))}
        </div>
    )
}

const DropdownItemRow = ({ item, onClose }: { item: DropdownItem; onClose: () => void }) => {
    const [submenuOpen, setSubmenuOpen] = useState(false)
    const isDanger = item.type === 'danger'
    const hasChecked = item.checked !== undefined
    const hasSubmenu = item.submenu && item.submenu.length > 0

    const handleClick = () => {
        if (item.disabled || hasSubmenu) return
        item.onClick?.()
        onClose()
    }

    return (
        <div
            className="relative"
            onMouseEnter={() => {
                if (hasSubmenu) setSubmenuOpen(true)
            }}
            onMouseLeave={() => {
                if (hasSubmenu) setSubmenuOpen(false)
            }}
        >
            <button
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={handleClick}
                className="flex min-h-9 w-full cursor-pointer items-center gap-3 rounded-[20px] border-none bg-transparent px-3 py-1.5 text-left transition-colors duration-100 outline-none enabled:hover:bg-default focus-visible:bg-default disabled:cursor-not-allowed disabled:opacity-50"
            >
                {/* Checkmark slot — always reserves space when any item uses checked */}
                {hasChecked && (
                    <span
                        className={`flex h-4 w-4 shrink-0 items-start pt-[3px] ${isDanger ? 'text-danger' : 'text-foreground'} ${item.checked ? 'opacity-100' : 'opacity-0'}`}
                    >
                        <Check size={16} strokeWidth={1.5} />
                    </span>
                )}

                {/* Prefix icon (only when no checked slot) */}
                {item.prefix && !hasChecked && (
                    <span className={`flex shrink-0 items-center ${isDanger ? 'text-danger' : 'text-foreground'}`}>{item.prefix}</span>
                )}

                {/* Label + description */}
                <div className="flex min-w-0 flex-1 flex-col">
                    <span className={`whitespace-nowrap text-sm font-medium leading-5 ${isDanger ? 'text-danger' : 'text-foreground'}`}>{item.label}</span>
                    {item.description && <span className="text-xs font-normal leading-4 text-foreground-muted">{item.description}</span>}
                </div>

                {/* Suffix: submenu chevron or custom suffix */}
                {hasSubmenu ? (
                    <span className="flex shrink-0 items-center text-foreground">
                        <ChevronRight size={16} strokeWidth={1.5} />
                    </span>
                ) : item.suffix ? (
                    <span className="flex shrink-0 items-center text-foreground-muted">{item.suffix}</span>
                ) : null}
            </button>

            {/* Submenu panel */}
            {hasSubmenu && submenuOpen && (
                <div className="absolute left-full top-0 z-[51]">
                    <DropdownPanel sections={item.submenu!} onClose={onClose} />
                </div>
            )}
        </div>
    )
}

export const Dropdown = ({ sections, trigger, style }: DropdownProps) => {
    const [open, setOpen] = useState(false)
    const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null)
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const panelRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        // Portaled to <body>, so the panel is no longer a DOM descendant of containerRef —
        // check both refs or a click inside the panel registers as "outside" and closes it.
        const handler = (e: MouseEvent) => {
            const target = e.target as Node
            if (containerRef.current?.contains(target)) return
            if (panelRef.current?.contains(target)) return
            setOpen(false)
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    // Runs before paint: measure the panel's real (content-driven) size against the anchor's
    // position — flip it above the trigger when there isn't room below (e.g. the last row of a
    // short table, where the menu previously ran off the bottom of the viewport with no way to
    // reach the cut-off items), and right-align it using its measured width. The panel is
    // shrink-to-fit (min 200px, like the pre-portal version), so the width can't be assumed —
    // a fixed width clipped long nowrap labels like "Resend Invite (Telegram)" behind a
    // horizontal scrollbar.
    useLayoutEffect(() => {
        if (!open || !anchorRect || !panelRef.current) return
        const panelHeight = panelRef.current.offsetHeight
        const panelWidth = panelRef.current.offsetWidth
        const spaceBelow = window.innerHeight - anchorRect.bottom
        const spaceAbove = anchorRect.top
        const left = Math.max(8, Math.min(window.innerWidth - panelWidth - 8, anchorRect.right - panelWidth))
        const top =
            spaceBelow >= panelHeight + 8 || spaceBelow >= spaceAbove
                ? Math.min(anchorRect.bottom + 8, window.innerHeight - panelHeight - 8)
                : Math.max(8, anchorRect.top - panelHeight - 8)
        setPos({ top, left })
    }, [open, anchorRect])

    // The panel is portaled to the end of <body>, so Tab from the trigger never reaches it. Opening moves focus onto
    // the first item (WAI-ARIA menu button); Escape and Tab hand it back to the trigger, or keyboard users lose their place.
    // Picking an item does not: it may open a Modal, which does not move focus, and a trigger left focused behind it would
    // reopen the menu above the Modal on the next Enter.
    useEffect(() => {
        if (!open || !pos) return
        panelRef.current?.querySelector<HTMLElement>('[role="menuitem"]:not(:disabled)')?.focus()
    }, [open, pos])

    const closeToTrigger = () => {
        setOpen(false)
        containerRef.current?.querySelector<HTMLElement>('button, [tabindex]:not([tabindex="-1"])')?.focus()
    }

    const handleEscape = (e: ReactKeyboardEvent) => {
        if (e.key !== 'Escape' || !open) return
        // Consume the Escape entirely: ds2 Modal listens for Escape on `document`, so without
        // stopping propagation one keypress would close both this menu AND the parent modal.
        // Same guard as ds2 Select/Autocomplete.
        e.stopPropagation()
        closeToTrigger()
    }

    const handlePanelKeyDown = (e: ReactKeyboardEvent) => {
        handleEscape(e)
        // Back on the trigger before the browser moves focus, so Tab continues from where the menu was opened.
        if (e.key === 'Tab') closeToTrigger()
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return
        const items = Array.from(panelRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') ?? [])
        if (items.length === 0) return
        e.preventDefault()
        const current = items.indexOf(document.activeElement as HTMLElement)
        const next =
            e.key === 'Home'
                ? 0
                : e.key === 'End'
                  ? items.length - 1
                  : e.key === 'ArrowDown'
                    ? (current + 1) % items.length
                    : (current - 1 + items.length) % items.length
        items[next].focus()
    }

    const toggleOpen = () => {
        if (open) {
            setOpen(false)
            return
        }
        const rect = containerRef.current?.getBoundingClientRect()
        if (rect) setAnchorRect(rect)
        setPos(null)
        setOpen(true)
    }

    return (
        <div ref={containerRef} className="relative inline-flex" style={style} onKeyDown={handleEscape}>
            <div onClick={toggleOpen} className="w-full cursor-pointer">
                {trigger}
            </div>

            {open &&
                anchorRect &&
                createPortal(
                    <div
                        ref={panelRef}
                        className={PANEL_CHROME}
                        onKeyDown={handlePanelKeyDown}
                        style={{
                            position: 'fixed',
                            // Hidden until the layout effect above measures the panel and sets
                            // `pos`, so these pre-measure coordinates are placeholders only.
                            top: pos ? pos.top : anchorRect.bottom + 8,
                            left: pos ? pos.left : anchorRect.left,
                            width: 'max-content',
                            maxWidth: 'calc(100vw - 16px)',
                            zIndex: POPOVER_Z_INDEX,
                            visibility: pos ? 'visible' : 'hidden',
                        }}
                    >
                        <div className="overflow-y-auto overscroll-contain rounded-3xl" style={{ maxHeight: 'calc(100vh - 16px)' }}>
                            <DropdownPanel sections={sections} onClose={() => setOpen(false)} bare />
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    )
}
